import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MedicationIcon from '@mui/icons-material/Medication';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import {
  resolvePrescribedProductId,
  vialNdcDiffersFromRx,
  vialProductMismatchesRx,
} from '../contexts/ScanSessionContext';
import useViewportCap from '../hooks/useViewportCap';
import EditableFields from './EditableFields';

const RX_FIELDS = [
  { key: 'rxNumber', label: 'Rx Number' },
  { key: 'patientName', label: 'Patient' },
  { key: 'drugName', label: 'Drug', required: true },
  { key: 'strength', label: 'Strength' },
  { key: 'ndc', label: 'NDC' },
  { key: 'quantity', label: 'Quantity' },
];

const VIAL_FIELDS = [
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'drugName', label: 'Drug', required: true },
  { key: 'strength', label: 'Strength' },
  { key: 'lotNumber', label: 'Lot #', required: true },
  { key: 'ndc', label: 'NDC', required: true },
  { key: 'productId', label: 'Product ID', required: true },
  { key: 'quantity', label: 'Quantity', required: true },
];

// A hand-recorded (unverified) vial also shows the BUD the operator read off
// the label — scanned vials never carry one, so the field is added only here.
const BUD_FIELD = { key: 'expirationDate', label: 'BUD' };

// Every vial row is read-only except quantity, which always needs adjusting
// by hand (nothing upstream knows how many are being dispensed). When the
// session's total dispensed quantity doesn't match the Rx, the quantity
// field also lights up (red border + helper text) so the operator can see
// which numbers to fix, on top of the banner above the vial list.
const fieldsForVial = (quantityMismatch, vial) => {
  const fields = VIAL_FIELDS.map((f) => {
    if (f.key !== 'quantity') return f;
    return quantityMismatch
      ? { ...f, alwaysEditable: true, warn: true, warnText: 'Total does not match the Rx' }
      : { ...f, alwaysEditable: true };
  });
  return vial?.fields?.recordedManually ? [...fields, BUD_FIELD] : fields;
};

// Why a vial row is flagged as an unverified lot — the three ways one arrives.
const unverifiedLotTooltip = (fields) => {
  if (fields.matchedFromScan) {
    return `Scanned lot is not in stock but is on record in inventory — matched from the scan, the dispensed quantity will post as a ${SYSTEM_DEFICIT.toLowerCase()} claim.`;
  }
  if (fields.inventoryLotId) {
    return `Recorded by hand against an existing inventory lot with no stock — the dispensed quantity will post as a ${SYSTEM_DEFICIT.toLowerCase()} claim.`;
  }
  return `Recorded by hand as a new lot inventory has never seen — it will be created and the dispensed quantity will post as a ${SYSTEM_DEFICIT.toLowerCase()} claim.`;
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
};

const CurrentSession = ({
  session,
  inventory,
  commitBlockReason,
  onEditVial,
  onAddVial,
  onRemoveVial,
  onCommit,
  onDiscard,
}) => {
  const { ref: scrollRef, maxHeight } = useViewportCap();
  const vialCount = session?.vials?.length ?? 0;
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [vialCount, scrollRef]);

  if (!session) {
    return (
      <Card variant='outlined' sx={{ width: '100%', height: '100%' }}>
        <CardContent>
          <Stack direction='row' spacing={1} alignItems='center' sx={{ color: 'text.secondary' }}>
            <ReceiptLongIcon fontSize='small' />
            <Typography>
              Scan an Rx bottle to start a session, or use “Enter Rx manually” for bottles that
              don&apos;t fit the scanner.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // The Rx's productId comes from resolvePrescribedProductId — that's what
  // every vial's productId is checked against, not anything read off the
  // vial's own label.
  const prescribedProductId = resolvePrescribedProductId(session.rx, inventory);
  const productMismatchAny = session.vials.some((v) =>
    vialProductMismatchesRx(prescribedProductId, v)
  );
  // Mirrors the quantity rule in commitBlockReason: a product mismatch means
  // the pharmacist may be substituting, so the dispensed total no longer
  // needs to equal the prescribed quantity.
  const dispensedQty = session.vials.reduce((sum, v) => sum + Number(v.fields?.quantity || 0), 0);
  const rxQty = Number(session.rx?.quantity);
  const quantityMismatch =
    !productMismatchAny &&
    Number.isFinite(rxQty) &&
    rxQty > 0 &&
    session.vials.length > 0 &&
    dispensedQty !== rxQty;

  return (
    <Card variant='outlined' sx={{ width: '100%' }}>
      <CardContent>
        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          justifyContent='space-between'
          sx={{ mb: 1 }}
        >
          <Stack direction='row' spacing={1} alignItems='center'>
            <ReceiptLongIcon color='primary' />
            <Typography variant='h6'>Current Rx Session</Typography>
            {session.rx.manualEntry && (
              <Chip size='small' variant='outlined' label='Manual entry' />
            )}
            {session.rx.validating && <Chip size='small' color='info' label='Validating…' />}
            <Typography variant='caption' color='text.secondary'>
              Started {formatTime(session.startedAt)}
            </Typography>
          </Stack>
          <Stack direction='row' spacing={1}>
            <Button size='small' color='inherit' onClick={() => setConfirmDiscardOpen(true)}>
              Discard
            </Button>
            <Tooltip title={commitBlockReason || ''}>
              <span>
                <Button
                  size='small'
                  variant='contained'
                  onClick={onCommit}
                  disabled={!!commitBlockReason}
                >
                  Commit session
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 1.5,
          }}
        >
          {RX_FIELDS.map(({ key, label }) => {
            const value = session.rx?.[key];
            const highlight = key === 'quantity' && quantityMismatch;
            return (
              <Box key={key}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  {label}
                </Typography>
                <Typography
                  variant='body2'
                  color={highlight ? 'error.main' : undefined}
                  fontWeight={highlight ? 700 : undefined}
                >
                  {value || '—'}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {quantityMismatch && (
          <Alert severity='warning' variant='filled' sx={{ mt: 2 }}>
            Dispensed quantity ({dispensedQty}) does not match the prescribed quantity ({rxQty}).
            Adjust the vial quantities before committing.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          justifyContent='space-between'
          sx={{ mb: 1 }}
        >
          <Stack direction='row' spacing={1} alignItems='center'>
            <MedicationIcon fontSize='small' />
            <Typography variant='subtitle1'>Drug Vials ({session.vials.length})</Typography>
          </Stack>
          <Tooltip title='For vials too large for the scanner — enter a lot number to add a read-only row matched from inventory.'>
            <Button size='small' startIcon={<AddIcon />} onClick={onAddVial}>
              Add drug manually
            </Button>
          </Tooltip>
        </Stack>

        <Box ref={scrollRef} sx={{ maxHeight, overflow: 'auto', pr: 0.5, pb: 10 }}>
          {session.vials.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              No vials scanned yet — scan a drug vial to add it to this Rx.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {session.vials.map((vial, idx) => {
                const productMismatch = vialProductMismatchesRx(prescribedProductId, vial);
                // Only worth surfacing when the productId itself still matches —
                // a productId mismatch already shows the stronger error above.
                const ndcMismatch = !productMismatch && vialNdcDiffersFromRx(session.rx, vial);
                return (
                  <Box
                    key={vial.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: (t) =>
                        `1px solid ${productMismatch ? t.palette.error.main : t.palette.divider}`,
                      bgcolor: 'background.default',
                    }}
                  >
                    <Stack
                      direction='row'
                      justifyContent='space-between'
                      alignItems='center'
                      sx={{ mb: 1 }}
                    >
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <Typography variant='subtitle2'>Vial #{idx + 1}</Typography>
                        {vial.fields.manualEntry && (
                          <Chip size='small' variant='outlined' label='Manual entry' />
                        )}
                        {vial.fields.recordedManually && (
                          <Tooltip title={unverifiedLotTooltip(vial.fields)}>
                            <Chip
                              size='small'
                              icon={<WarningAmberIcon />}
                              label={`${SYSTEM_DEFICIT} lot`}
                              sx={{ bgcolor: 'warning.warningColour' }}
                            />
                          </Tooltip>
                        )}
                        {vial.fields.unsure && (
                          <Chip
                            size='small'
                            sx={{ bgcolor: 'warning.warningColour' }}
                            label={SYSTEM_DEFICIT}
                          />
                        )}
                        {productMismatch && (
                          <Tooltip
                            title={
                              prescribedProductId
                                ? `Vial product id "${vial.fields.productId}" does not match the Rx's prescribed product id "${prescribedProductId}".`
                                : `Could not verify vial product id "${vial.fields.productId}" — the Rx's prescribed NDC has no match in inventory.`
                            }
                          >
                            <Chip
                              size='small'
                              color='error'
                              icon={<WarningAmberIcon />}
                              label='Drug mismatch'
                            />
                          </Tooltip>
                        )}
                        {ndcMismatch && (
                          <Tooltip
                            title={`Vial NDC "${vial.fields.ndc}" differs from the Rx's prescribed NDC "${session.rx.ndc}" — the product id still matches, so this doesn't block committing.`}
                          >
                            <Chip
                              size='small'
                              icon={<WarningAmberIcon />}
                              label='NDC differs'
                              sx={{ bgcolor: 'warning.warningColour' }}
                            />
                          </Tooltip>
                        )}
                        <Typography variant='caption' color='text.secondary'>
                          {formatTime(vial.scannedAt)}
                        </Typography>
                      </Stack>
                      <IconButton
                        size='small'
                        onClick={() => onRemoveVial(vial.id)}
                        aria-label='Remove vial'
                      >
                        <DeleteOutlineIcon fontSize='small' />
                      </IconButton>
                    </Stack>
                    {productMismatch && (
                      <Alert severity='error' variant='filled' sx={{ py: 0, mb: 2 }}>
                        Not the prescribed drug — this vial&apos;s product doesn&apos;t match the
                        Rx&apos;s prescribed product id.
                      </Alert>
                    )}
                    <EditableFields
                      fields={fieldsForVial(quantityMismatch, vial)}
                      values={vial.fields}
                      onChange={(patch) => onEditVial(vial.id, patch)}
                      disabled
                    />
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </CardContent>

      <Dialog
        open={confirmDiscardOpen}
        onClose={() => setConfirmDiscardOpen(false)}
        aria-labelledby='discard-session-title'
      >
        <DialogTitle id='discard-session-title'>Discard this Rx session?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {vialCount > 0
              ? `This will discard the current Rx${
                  session?.rx?.rxNumber ? ` (${session.rx.rxNumber})` : ''
                } and ${vialCount} scanned ${
                  vialCount === 1 ? 'vial' : 'vials'
                }. This can't be undone.`
              : `This will discard the current Rx${
                  session?.rx?.rxNumber ? ` (${session.rx.rxNumber})` : ''
                }. This can't be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscardOpen(false)} color='inherit'>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmDiscardOpen(false);
              onDiscard();
            }}
            color='error'
            variant='contained'
            autoFocus
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

CurrentSession.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.string.isRequired,
    startedAt: PropTypes.string,
    rx: PropTypes.shape({
      unsure: PropTypes.bool,
      drugName: PropTypes.string,
      validating: PropTypes.bool,
      manualEntry: PropTypes.bool,
      rxNumber: PropTypes.string,
      ndc: PropTypes.string,
      quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    vials: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({
          unsure: PropTypes.bool,
          drugName: PropTypes.string,
          manualEntry: PropTypes.bool,
          fromInventory: PropTypes.bool,
          recordedManually: PropTypes.bool,
          matchedFromScan: PropTypes.bool,
          inventoryLotId: PropTypes.string,
          expirationDate: PropTypes.string,
          ndc: PropTypes.string,
          productId: PropTypes.string,
        }),
      })
    ).isRequired,
  }),
  inventory: PropTypes.arrayOf(
    PropTypes.shape({ ndc: PropTypes.string, productId: PropTypes.string })
  ),
  commitBlockReason: PropTypes.string,
  onEditVial: PropTypes.func.isRequired,
  onAddVial: PropTypes.func.isRequired,
  onRemoveVial: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
};

CurrentSession.defaultProps = {
  session: null,
  inventory: [],
  commitBlockReason: '',
};

export default CurrentSession;
